import { createContext, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "@/lib/apiFetch";

export type User = {
  id: number;
  name: string;
  email: string;
  role: "admin" | "user";
  company_id?: number | null;
  // add any other fields you return from /api/user
};

interface AuthContextType {
  /** Preferred alias in components */
  me: User | null;
  /** Backward-compat alias (same value as `me`) */
  user: User | null;

  isAuthenticated: boolean;
  loading: boolean;

  /** (Re)fetch the current session user */
  refresh: () => Promise<void>;

  /** Login flow */
  login: (email: string, password: string) => Promise<void>;

  /** Logout flow */
  logout: () => Promise<void>;

  /** Legacy helper */
  getUser: () => User | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  // initialize from localStorage to avoid flicker
  const [me, setMe] = useState<User | null>(() => {
    try {
      const stored = localStorage.getItem("user");
      return stored ? (JSON.parse(stored) as User) : null;
    } catch {
      return null;
    }
  });
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(
    localStorage.getItem("isAuthenticated") === "true"
  );
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();

  const persist = (user: User | null) => {
    if (user) {
      localStorage.setItem("user", JSON.stringify(user));
      localStorage.setItem("isAuthenticated", "true");
    } else {
      localStorage.removeItem("user");
      localStorage.removeItem("isAuthenticated");
    }
  };

  /** Fetch /api/user and update state */
  const refresh = async () => {
    setLoading(true);
    try {
      // Ensure Sanctum cookie exists (no-op if already set)
      await apiFetch("/sanctum/csrf-cookie");
      const res = await apiFetch("/api/user", { suppressRedirect: true });
      setMe(res as User);
      setIsAuthenticated(true);
      persist(res as User);
    } catch {
      setMe(null);
      setIsAuthenticated(false);
      persist(null);
    } finally {
      setLoading(false);
    }
  };

  // Check session on mount
  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (username: string, password: string) => {
    await apiFetch("/sanctum/csrf-cookie");

    const res = await apiFetch("/api/login", {
      method: "POST",
      suppressRedirect: true,
      body: { username, password },
    });

    // Support either { user, message } or full user payload
    const nextUser: User | null = (res?.user as User) ?? (res as User) ?? null;
    if (!nextUser) {
      throw new Error(res?.message || "Login failed");
    }

    setMe(nextUser);
    setIsAuthenticated(true);
    persist(nextUser);
  };

  const logout = async () => {
    try {
      const res = await fetch("/api/logout", {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) {
        console.warn("Logout request failed:", res.status);
      }
    } catch (err) {
      console.error("Logout error:", err);
    } finally {
      setMe(null);
      setIsAuthenticated(false);
      persist(null);
      navigate("/login", { replace: true });
    }
  };

  const getUser = () => me;

  return (
    <AuthContext.Provider
      value={{
        me,
        user: me, // alias for backward compatibility
        isAuthenticated,
        loading,
        refresh,
        login,
        logout,
        getUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
};
